/**
 * Availability on Home — two pieces sharing one fetch via useAvailability():
 *   1. MyWeekCard — the user's own recurring grid, tap-to-toggle (PUT on tap).
 *      Rendered at the very top of Home so it stays "smack in the face".
 *   2. ClubAvailability — one read-only grid per club, lower on the page;
 *      per-cell counts, tap a cell to see who + a "Set up a game" chat handoff.
 *
 * The grid is a recurring weekly template ("I can generally play then"), not a
 * calendar — sessions/RSVPs handle specific dates. Grids untouched for 30+ days
 * drop out of the club aggregate server-side (shown as "out of date").
 */
import { Fragment, useEffect, useRef, useState } from "react";

import { track } from "../analytics";
import {
  getAvailabilityHome,
  putMyAvailability,
  type AvailabilityBracket,
  type AvailabilityGrid,
  type AvailabilityHome,
  type GroupAvailability,
} from "../api";

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
const BRACKETS = ["morning", "afternoon", "evening"] as const;
type Day = (typeof DAYS)[number];
const DAY_SHORT: Record<Day, string> = {
  mon: "M", tue: "T", wed: "W", thu: "T", fri: "F", sat: "S", sun: "S",
};
const DAY_FULL: Record<Day, string> = {
  mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday",
  fri: "Friday", sat: "Saturday", sun: "Sunday",
};
const BRACKET_SHORT: Record<AvailabilityBracket, string> = {
  morning: "Morn", afternoon: "Aft", evening: "Eve",
};

function hasBracket(grid: AvailabilityGrid, day: string, bracket: AvailabilityBracket): boolean {
  return (grid[day] ?? []).includes(bracket);
}

function toggled(grid: AvailabilityGrid, day: string, bracket: AvailabilityBracket): AvailabilityGrid {
  const current = grid[day] ?? [];
  const next = current.includes(bracket)
    ? current.filter((b) => b !== bracket)
    : [...BRACKETS].filter((b) => b === bracket || current.includes(b));
  const out = { ...grid };
  if (next.length) out[day] = next;
  else delete out[day];
  return out;
}

function daysAgo(iso: string | null): number | null {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}

function agoLabel(iso: string | null): string {
  const d = daysAgo(iso);
  if (d == null) return "not set yet";
  if (d <= 0) return "updated today";
  if (d < 7) return `updated ${d}d ago`;
  return `updated ${Math.floor(d / 7)}w ago`;
}

export interface AvailabilityState {
  data: AvailabilityHome | null;
  error: string | null;
  save: (grid: AvailabilityGrid) => Promise<void>;
}

/** One fetch shared by MyWeekCard (top of Home) and ClubAvailability (lower) —
 * a toggle in the former updates the club counts in the latter. Pass
 * `enabled: false` when the user has ✕-ed the feature off to skip the fetch. */
export function useAvailability(enabled: boolean): AvailabilityState {
  const [data, setData] = useState<AvailabilityHome | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Monotonic op counter — rapid taps each fire a PUT + aggregate refetch; only
  // the latest op's refetch is applied so a slow earlier response can't clobber.
  const opRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    getAvailabilityHome()
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError(String((e as Error).message)));
    return () => { cancelled = true; };
  }, [enabled]);

  async function save(grid: AvailabilityGrid) {
    const op = ++opRef.current;
    const prev = data;
    setData((d) => (d ? { ...d, mine: grid } : d));
    setError(null);
    try {
      const saved = await putMyAvailability(grid);
      const fresh = await getAvailabilityHome(); // club counts include me
      if (opRef.current === op) {
        setData({ ...fresh, mine: saved.availability, updated_at: saved.updated_at });
      }
    } catch (e) {
      if (opRef.current === op) {
        setData(prev);
        setError(String((e as Error).message));
      }
    }
  }

  return { data, error, save };
}

export function MyWeekCard({
  avail,
  firstName,
  onDismiss,
}: {
  avail: AvailabilityState;
  /** Real first name, or null when the session has none — falls back to "Your week". */
  firstName: string | null;
  /** Hides the whole availability feature (this card + club grids). The
   *  "My availability" profile-menu item brings it back. */
  onDismiss: () => void;
}) {
  const { data, error, save } = avail;
  // Inline ✕ confirmation — names the way back (profile menu) before hiding,
  // so dismissal isn't a one-way door people forget exists.
  const [confirmingHide, setConfirmingHide] = useState(false);
  if (!data) return null;

  const mineEmpty = Object.keys(data.mine).length === 0;
  const age = daysAgo(data.updated_at);
  const showConfirm = !mineEmpty && age != null && age >= 14;

  return (
    // Same shell as the Getting-started box: dark header with the ✕ pinned
    // top-right, content below — dismissible feature boxes share one identity.
    <div className="cs-reveal mb-6 border-2 border-ink rounded-md overflow-hidden">
      <div className="px-5 py-4 bg-ink text-bg flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-bg/60">
            Your availability · only you can edit
          </div>
          <div className="font-display font-bold text-lg leading-tight truncate">
            {firstName ? `${firstName}'s week` : "Your week"}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="font-mono text-[10px] uppercase tracking-wider text-bg/50">
            {agoLabel(data.updated_at)}
          </span>
          {showConfirm && (
            <button
              onClick={() => void save(data.mine)}
              className="cs-tap px-2 py-0.5 border border-bg/30 rounded-sm hover:border-bg text-bg/70 hover:text-bg font-mono text-[10px] uppercase tracking-wider"
            >
              still right? ✓
            </button>
          )}
          <button
            onClick={() => setConfirmingHide(true)}
            aria-label="Hide availability"
            title="Hide availability — bring it back from the profile menu"
            className="shrink-0 text-bg/50 hover:text-bg cs-tap font-mono text-lg leading-none"
          >
            ✕
          </button>
        </div>
      </div>
      <div className="p-4 bg-bg">
        <Grid
          renderCell={(day, bracket) => {
            const on = hasBracket(data.mine, day, bracket);
            return (
              <button
                aria-pressed={on}
                title={`${DAY_FULL[day]} ${bracket}`}
                onClick={() => void save(toggled(data.mine, day, bracket))}
                className={
                  "cs-tap h-8 w-full rounded-sm border transition-colors " +
                  (on
                    ? "bg-accent border-ink"
                    : "border-ink/15 hover:border-ink/45 bg-transparent")
                }
              />
            );
          }}
        />
        {confirmingHide ? (
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <span className="text-xs text-ink/65">
              Hide availability? You can bring it back anytime from the profile
              menu (top right) → <span className="font-bold">My availability</span>.
            </span>
            <button
              onClick={() => {
                track("availability_hidden", { had_grid: !mineEmpty });
                onDismiss();
              }}
              className="cs-tap px-3 py-1.5 bg-ink text-bg font-display font-bold text-xs rounded-sm"
            >
              Hide it
            </button>
            <button
              onClick={() => setConfirmingHide(false)}
              className="cs-tap font-mono text-[11px] uppercase tracking-wider text-ink/45"
            >
              Keep it
            </button>
          </div>
        ) : (
          <div className="mt-2 text-xs text-ink/55">
            {mineEmpty
              ? "Tap the times you can usually play. Clubmates use it to find you and set up games — it's only visible inside your club."
              : "This tells clubmates when they can pair up with you, so keep it honest. Only your club can see it."}
          </div>
        )}
        {error && (
          <div className="mt-2 font-mono text-[11px] text-warn">{error}</div>
        )}
      </div>
    </div>
  );
}

export function ClubAvailability({
  avail,
  onAskAgent,
}: {
  avail: AvailabilityState;
  onAskAgent: (prefill?: string) => void;
}) {
  const { data } = avail;
  if (!data || data.groups.length === 0) return null;
  return (
    <div className="mb-6">
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink/55 mb-3">
        Club availability
      </div>
      {data.groups.map((g) => (
        <ClubGrid key={g.group_id} group={g} onAskAgent={onAskAgent} />
      ))}
    </div>
  );
}

// Shared 8-column scaffold: row-label gutter + 7 day columns.
function Grid({
  renderCell,
}: {
  renderCell: (day: Day, bracket: AvailabilityBracket) => React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[44px_repeat(7,1fr)] gap-1 items-center">
      <div />
      {DAYS.map((d) => (
        <div
          key={d}
          className="text-center font-mono text-[10px] uppercase tracking-wider text-ink/55"
        >
          <span className="hidden sm:inline">{DAY_FULL[d].slice(0, 3)}</span>
          <span className="sm:hidden">{DAY_SHORT[d]}</span>
        </div>
      ))}
      {BRACKETS.map((b) => (
        <Fragment key={b}>
          <div className="font-mono text-[10px] uppercase tracking-wider text-ink/55">
            {BRACKET_SHORT[b]}
          </div>
          {DAYS.map((d) => (
            <div key={d}>{renderCell(d, b)}</div>
          ))}
        </Fragment>
      ))}
    </div>
  );
}

function ClubGrid({
  group,
  onAskAgent,
}: {
  group: GroupAvailability;
  onAskAgent: (prefill?: string) => void;
}) {
  const [selected, setSelected] = useState<{ day: Day; bracket: AvailabilityBracket } | null>(null);
  // One-time teaching pulse on the busiest cell, until the first tap.
  const [interacted, setInteracted] = useState(false);

  const names = selected ? group.cells[selected.day]?.[selected.bracket] ?? [] : [];

  // Busiest cell — the pulse target that invites the first tap.
  let hot: { day: Day; bracket: AvailabilityBracket } | null = null;
  let hotCount = 0;
  for (const d of DAYS) {
    for (const b of BRACKETS) {
      const n = group.cells[d]?.[b]?.length ?? 0;
      if (n > hotCount) {
        hotCount = n;
        hot = { day: d, bracket: b };
      }
    }
  }

  return (
    <div className="cs-reveal border border-ink/15 rounded-md p-4 mb-3 bg-bg">
      <div className="flex items-baseline justify-between gap-3 mb-1 flex-wrap">
        <div className="font-display font-bold text-sm">{group.name}</div>
        <div className="font-mono text-[10px] uppercase tracking-wider text-ink/45">
          {group.members_set} of {group.total_members} members shared their week
          {group.members_stale > 0 && ` · ${group.members_stale} out of date`}
        </div>
      </div>
      <div className="text-xs text-ink/55 mb-3">
        How many club members can usually play each time — tap a number to see
        who, then round up a game.
      </div>
      {group.members_set === 0 ? (
        <div className="font-mono text-[11px] text-ink/55">
          No one's set their availability yet — yours counts as soon as you tap
          your grid at the top of this page.
        </div>
      ) : (
        <>
          <Grid
            renderCell={(day, bracket) => {
              const count = group.cells[day]?.[bracket]?.length ?? 0;
              const isSel = selected?.day === day && selected?.bracket === bracket;
              const isHot =
                !interacted &&
                hot?.day === day &&
                hot?.bracket === bracket;
              return (
                <button
                  onClick={() => {
                    setInteracted(true);
                    if (!isSel) {
                      track("availability_club_cell_tapped", {
                        group_id: group.group_id,
                        day,
                        bracket,
                        count,
                      });
                    }
                    setSelected(isSel ? null : { day, bracket });
                  }}
                  title={`${DAY_FULL[day]} ${bracket} — ${count} free`}
                  className={
                    "cs-tap h-8 w-full rounded-sm border font-mono text-[11px] num-tab transition-colors " +
                    (isSel
                      ? "bg-ink text-bg border-ink font-bold"
                      : count > 0
                        ? "border-ink/30 font-bold hover:border-ink"
                        : "border-ink/10 text-ink/25 hover:border-ink/30") +
                    (isHot ? " ring-2 ring-accent animate-pulse" : "")
                  }
                >
                  {count}
                </button>
              );
            }}
          />
          {selected && (
            <div className="mt-3 border-t border-ink/10 pt-3 flex items-center gap-3 flex-wrap">
              <div className="min-w-0 flex-1 text-sm">
                <span className="font-display font-bold">
                  {DAY_FULL[selected.day]} {selected.bracket}
                </span>{" "}
                <span className="text-ink/65">
                  — {names.length ? names.join(", ") : "no one yet"}
                </span>
              </div>
              {names.length > 0 && (
                <button
                  onClick={() => {
                    track("availability_setup_game_clicked", {
                      group_id: group.group_id,
                      day: selected.day,
                      bracket: selected.bracket,
                      names_count: names.length,
                    });
                    onAskAgent(
                      `I want to play in ${group.name} on ${DAY_FULL[selected.day]} ${selected.bracket} — ` +
                        `${names.join(", ")} are usually free then. Send a pickup beacon to whoever's around.`,
                    );
                  }}
                  className="cs-tap shrink-0 px-3 py-1.5 bg-accent text-ink text-[11px] uppercase tracking-wider font-bold rounded-sm"
                >
                  Find players →
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
